import { Row, Col } from "react-bootstrap";
import ContentHeader from "../../../layout/ContenteHeader";
import InfoBox from "../../../layout/Boxes/InfoBox";
import {
  faMoneyBill,
  faList,
  faHourglass,
  faFileInvoiceDollar,
  faGaugeHigh,
  faArrowUpRightFromSquare,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate } from "react-router-dom";
import TodoIndex from "../todo/Index";
import ProjectDashboard from "./ProjectDashboard.jsx";
import { useEffect, useState } from "react";
import crudApi from "@serie3/common/api/crudApi";
import TimetrackingConfig from "@serie3/freelancesplaceapi/Config/Timetracking/TimetrackingConfig";
import ProjectConfig from "@serie3/freelancesplaceapi/Config/Projects/ProjectConfig.js";
import billingApi from "@serie3/freelancesplaceapi/api/finance/out";
import BillView from "../finance/modals/BillView";
import BillOut from "../finance/BillOut";

const ageDays = (dateStr) => {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
};

const ageColor = (days) => {
  if (days > 60) return { color: "#b71c1c", bg: "#ffebee" };
  if (days > 30) return { color: "#e65100", bg: "#fff3e0" };
  return { color: "#1a73e8", bg: "#e8f0fe" };
};

const getContactName = (bill) => {
  if (bill.person) return [bill.person.vorname, bill.person.name].filter(Boolean).join(" ");
  if (bill.kunde) return bill.kunde.firmenname || "—";
  return "—";
};

const MainDashboard = () => {
  const [totalIncome, setTotalIncome] = useState(0);
  const [doneTickets, setDoneTickets] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [unpaidBills, setUnpaidBills] = useState([]);
  const [dashboardProjects, setDashboardProjects] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const navigate = useNavigate();

  const openBill = async (stub) => {
    const full = await billingApi.showBill("bills", "out", stub.id);
    setSelectedBill(full);
    setShowBillModal(true);
  };

  const year = new Date().getFullYear();

  useEffect(() => {
    const loadData = async () => {
      const [incomeRes, hoursRes, billsRes] = await Promise.all([
        crudApi.justGet("/finance/simplebalance/"),
        crudApi.justGet(TimetrackingConfig.endpoints.totalMonthlyHours),
        billingApi.getAll("bills", "out", year),
      ]);

      if (incomeRes.status === 200)
        setTotalIncome(parseFloat(incomeRes.data).toFixed(2));
      if (hoursRes.status === 200)
        setTotalHours(hoursRes.data);
      if (billsRes.status === 200) {
        const all = billsRes.data.finanzvorgang || [];
        setUnpaidBills(all.filter((b) => !b.geldeingang));
      }
    };

    const loadProjects = async () => {
      await crudApi.getAll(
        ProjectConfig.endpoints.showindashboard,
        setDashboardProjects,
        "project"
      );
    };

    loadData();
    loadProjects();
  }, []);

  const totalOutstanding = unpaidBills.reduce((s, b) => s + (b.brutto || 0), 0);

  return (
    <>
      <BillView
        show={showBillModal}
        setShow={setShowBillModal}
        bill={selectedBill}
        direction="out"
        extraFooter={
          selectedBill?.draft ? (
            <button
              onClick={() => { setShowBillModal(false); navigate(`/finance/out/bills/edit/${selectedBill.id}`); }}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 16px", background: "#e8f0fe", color: "#1a73e8",
                border: "none", borderRadius: "8px", fontWeight: 600,
                fontSize: "0.88rem", cursor: "pointer",
              }}
            >
              <FontAwesomeIcon icon={faEdit} /> Edit
            </button>
          ) : null
        }
      >
        {selectedBill && <BillOut bill={selectedBill} />}
      </BillView>

      <ContentHeader>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "46px", height: "46px", borderRadius: "12px",
            background: "linear-gradient(135deg, #1a73e8 0%, #1a237e 100%)",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.2rem", flexShrink: 0, boxShadow: "0 4px 12px rgba(26,115,232,0.3)",
          }}>
            <FontAwesomeIcon icon={faGaugeHigh} />
          </div>
          <div>
            <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#212121", lineHeight: 1.2 }}>Dashboard</div>
            <div style={{ fontSize: "0.85rem", color: "#9e9e9e", marginTop: "2px" }}>Overview of your work and activity</div>
          </div>
        </div>
      </ContentHeader>

      <section className="content">
        <Row>
          <Col className="col-sm-6 col-md-3 col-12">
            <InfoBox color="bg-info" icon={faMoneyBill} title="Income" message={totalIncome + " €"} textStyle={{ color: totalIncome > 0 ? "black" : "darkred" }} />
          </Col>
          <Col className="col-sm-6 col-md-3 col-12">
            <InfoBox color="bg-success" icon={faList} title="Done" message={doneTickets + " %"} />
          </Col>
          <Col className="col-sm-6 col-md-3 col-12">
            <InfoBox color="bg-warning" icon={faHourglass} title="Total Hours" message={totalHours + " h"} />
          </Col>
          <Col className="col-sm-6 col-md-3 col-12">
            <InfoBox
              color="bg-danger"
              icon={faFileInvoiceDollar}
              title="Outstanding"
              message={unpaidBills.length > 0 ? totalOutstanding.toFixed(2) + " €" : "All paid"}
            />
          </Col>
        </Row>

        <Row>
          {/* Todo panel */}
          <Col sm={8} md={8} style={{ height: "450px" }}>
            <div style={{
              background: "#fff", borderRadius: "12px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.09)",
              height: "100%", overflow: "hidden", display: "flex", flexDirection: "column",
            }}>
              <TodoIndex show />
            </div>
          </Col>

          {/* Outstanding invoices panel */}
          <Col sm={4} md={4} style={{ height: "450px" }}>
            <div style={{
              background: "#fff", borderRadius: "12px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.09)",
              height: "100%", overflow: "hidden", display: "flex", flexDirection: "column",
            }}>
              {/* Header */}
              <div style={{
                padding: "14px 16px 12px",
                background: unpaidBills.length > 0
                  ? "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)"
                  : "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)",
                flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#fff", display: "flex", alignItems: "center", gap: "7px" }}>
                    <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    Outstanding Invoices
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)", marginTop: "3px" }}>
                    {unpaidBills.length > 0
                      ? `${unpaidBills.length} unpaid · ${totalOutstanding.toFixed(2)} € total`
                      : "All invoices paid"}
                  </div>
                </div>
                <Link
                  to="/finance/out/bills"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    padding: "5px 10px", background: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.25)", borderRadius: "7px",
                    color: "#fff", fontSize: "0.72rem", fontWeight: 600, textDecoration: "none",
                  }}
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: "0.6rem" }} />
                  All
                </Link>
              </div>

              {/* List */}
              <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
                {unpaidBills.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "48px 0", color: "#bdbdbd", fontSize: "0.85rem" }}>
                    No outstanding invoices
                  </div>
                ) : (
                  unpaidBills.map((bill, i) => {
                    const days = ageDays(bill.datum);
                    const { color, bg } = ageColor(days);
                    return (
                      <div key={i} onClick={() => openBill(bill)} style={{
                        display: "flex", alignItems: "center", gap: "10px",
                        padding: "9px 10px", borderRadius: "9px", marginBottom: "5px",
                        background: "#fafafa", border: "1px solid #f0f0f0",
                        cursor: "pointer", transition: "background 0.12s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fafafa"}
                      >
                        {/* Age badge */}
                        <div style={{
                          flexShrink: 0, minWidth: "42px", textAlign: "center",
                          background: bg, color, borderRadius: "7px", padding: "4px 6px",
                        }}>
                          <div style={{ fontWeight: 800, fontSize: "0.82rem", lineHeight: 1 }}>{days}</div>
                          <div style={{ fontSize: "0.58rem", fontWeight: 600, opacity: 0.8 }}>days</div>
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: "0.82rem", color: "#212121", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {getContactName(bill)}
                          </div>
                          <div style={{ fontSize: "0.7rem", color: "#9e9e9e", marginTop: "1px" }}>
                            #{bill.rechnungs_nummer}
                          </div>
                        </div>

                        {/* Amount */}
                        <div style={{ flexShrink: 0, textAlign: "right" }}>
                          <div style={{ fontWeight: 800, fontSize: "0.88rem", color: "#c62828" }}>
                            {(bill.brutto || 0).toFixed(2)} €
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Col>
        </Row>

        <br />

        {dashboardProjects.map((project) => (
          <ProjectDashboard
            key={project.id}
            stub={project}
            setDoneTickets={setDoneTickets}
            onUnpin={() => setDashboardProjects((prev) => prev.filter((p) => p.id !== project.id))}
          />
        ))}
      </section>
    </>
  );
};

export default MainDashboard;
